// ABOUTME: Renders the variant pricing execution trace as node → procedure → rule tables.
// ABOUTME: Owns the "Show skipped rules" toggle; shared by the success and error views of the drawer.

import { Box, Chip, FormControlLabel, Stack, Switch, Typography } from '@ds/coolshop';
// DS Accordion only accepts a string summary; the procedure header needs JSX (code + rule count).
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { alpha, Theme } from '@mui/material/styles';
// dnd-kit-style tables are not wrapped by the DS; the rule trace needs a real table with sx.
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ExplainNodeEntry, ExplainRuleEntry } from './useExplainPrice';

const MONO = { fontFamily: 'monospace' } as const;

// Renders an operand value read by a `when` guard. An unset operand comes back as null (normalised
// server-side so it survives JSON) and reads as "∅"; everything else is shown verbatim.
const formatOperandValue = (value: unknown): string => {
    if (value === null || value === undefined) return '∅';
    if (typeof value === 'string') return value === '' ? '""' : value;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

function RuleStatus({ rule }: { rule: ExplainRuleEntry }) {
    const { formatMessage } = useIntl();

    if (!rule.fired) {
        return (
            <Chip
                size="small"
                label={formatMessage({
                    id: 'cpq.pricing-trace.skipped',
                    defaultMessage: 'skipped',
                })}
            />
        );
    }
    if (rule.unpriced) {
        return (
            <Chip
                size="small"
                color="warning"
                label={formatMessage({
                    id: 'cpq.pricing-trace.unpriced',
                    defaultMessage: 'no price',
                })}
            />
        );
    }
    // A rule that fired and priced normally is the common case; a chip on every such row is noise, so
    // the absence of a chip (and a full-opacity, untinted row) is the "fired normally" signal.
    return <span>—</span>;
}

// The operands a false `when` guard read, so a reader can see why the rule was skipped. Rendered
// inline in the Operands column as `path = value` lines; falls back to the full scoped bag (marked
// as such) when the engine could not isolate the referenced paths.
function OperandsCell({ rule }: { rule: ExplainRuleEntry }) {
    const { formatMessage } = useIntl();
    const { skipReason } = rule;

    if (!skipReason || skipReason.operands.length === 0) {
        return <TableCell sx={MONO}>—</TableCell>;
    }

    return (
        <TableCell sx={MONO}>
            <Stack gap={0.25}>
                {skipReason.fallback && (
                    <Typography variant="caption" color="textSecondary">
                        {formatMessage({
                            id: 'cpq.pricing-trace.operands.fallback',
                            defaultMessage: '(full scope)',
                        })}
                    </Typography>
                )}
                {skipReason.operands.map(operand => (
                    <Typography key={operand.path} variant="caption" color="textSecondary">
                        {`${operand.path} = ${formatOperandValue(operand.value)}`}
                    </Typography>
                ))}
            </Stack>
        </TableCell>
    );
}

function RuleTableRow({ rule }: { rule: ExplainRuleEntry }) {
    // Skipped rules are de-emphasised; rules that fired but found no price are tinted so the gap in
    // the running total is visible at a glance.
    const rowSx = {
        ...(!rule.fired ? { opacity: 0.55 } : {}),
        ...(rule.unpriced
            ? { backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.1) }
            : {}),
    };

    const identifier = rule.emittedKey ?? rule.writtenTemp ?? rule.tableRef ?? rule.key ?? '—';

    return (
        <TableRow sx={rowSx}>
            <TableCell>{`#${rule.ruleIndex} ${rule.type}`}</TableCell>
            <TableCell>
                <RuleStatus rule={rule} />
            </TableCell>
            <TableCell sx={MONO}>{identifier}</TableCell>
            <TableCell>{rule.bucket ?? rule.target ?? '—'}</TableCell>
            <TableCell sx={MONO}>
                {rule.when ? (
                    <Typography variant="caption" color="textSecondary">
                        {rule.when}
                    </Typography>
                ) : (
                    '—'
                )}
            </TableCell>
            {/* Why a skipped rule's `when` was false: the operands it read and their values. */}
            <OperandsCell rule={rule} />
            <TableCell align="right" sx={MONO}>
                {rule.appliedFactor !== undefined ? `×${rule.appliedFactor}` : ''}
            </TableCell>
            <TableCell align="right" sx={MONO}>
                {rule.amount !== undefined ? rule.amount.toFixed(2) : ''}
            </TableCell>
            <TableCell align="right" sx={MONO}>
                {rule.runningTotal !== undefined ? rule.runningTotal.toFixed(2) : ''}
            </TableCell>
        </TableRow>
    );
}

function RuleTable({ rules }: { rules: ExplainRuleEntry[] }) {
    const { formatMessage } = useIntl();

    const columns = [
        {
            id: 'rule',
            label: formatMessage({ id: 'cpq.pricing-trace.col.rule', defaultMessage: 'Rule' }),
            align: 'left' as const,
        },
        {
            id: 'status',
            label: formatMessage({ id: 'cpq.pricing-trace.col.status', defaultMessage: 'Status' }),
            align: 'left' as const,
        },
        {
            id: 'key',
            label: formatMessage({
                id: 'cpq.pricing-trace.col.key',
                defaultMessage: 'Key / target',
            }),
            align: 'left' as const,
        },
        {
            id: 'bucket',
            label: formatMessage({ id: 'cpq.pricing-trace.col.bucket', defaultMessage: 'Bucket' }),
            align: 'left' as const,
        },
        {
            id: 'condition',
            label: formatMessage({
                id: 'cpq.pricing-trace.col.condition',
                defaultMessage: 'Condition',
            }),
            align: 'left' as const,
        },
        {
            id: 'operands',
            label: formatMessage({
                id: 'cpq.pricing-trace.col.operands',
                defaultMessage: 'Operands',
            }),
            align: 'left' as const,
        },
        {
            id: 'factor',
            label: formatMessage({ id: 'cpq.pricing-trace.col.factor', defaultMessage: 'Factor' }),
            align: 'right' as const,
        },
        {
            id: 'amount',
            label: formatMessage({ id: 'cpq.pricing-trace.col.amount', defaultMessage: 'Amount' }),
            align: 'right' as const,
        },
        {
            id: 'total',
            label: formatMessage({
                id: 'cpq.pricing-trace.col.total',
                defaultMessage: 'Running total',
            }),
            align: 'right' as const,
        },
    ];

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map(column => (
                            <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rules.map(rule => (
                        <RuleTableRow key={rule.ruleIndex} rule={rule} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export function TraceNodeList({ nodes }: { nodes: ExplainNodeEntry[] }) {
    const { formatMessage } = useIntl();
    const [showSkipped, setShowSkipped] = useState(false);

    return (
        <Stack gap={2}>
            <FormControlLabel
                control={
                    <Switch
                        checked={showSkipped}
                        onChange={(_, checked) => setShowSkipped(checked)}
                    />
                }
                label={formatMessage({
                    id: 'cpq.pricing-trace.show-skipped',
                    defaultMessage: 'Show skipped rules',
                })}
            />

            {nodes.map(node => (
                <Stack key={node.nodeId} gap={1}>
                    <Typography variant="subtitle2">
                        {`${node.nodeId} · ${node.modelId}`}
                    </Typography>
                    {node.procedures.map(procedure => {
                        // The server keeps every rule the engine touched; the default view is what
                        // actually fired.
                        const firedCount = procedure.rules.filter(rule => rule.fired).length;
                        const rules = showSkipped
                            ? procedure.rules
                            : procedure.rules.filter(rule => rule.fired);
                        return (
                            <Accordion key={`${node.nodeId}-${procedure.code}`} defaultExpanded>
                                <AccordionSummary>
                                    <Stack direction="row" gap={1} alignItems="center">
                                        <Typography variant="body1">
                                            <span style={MONO}>{procedure.code}</span>
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatMessage(
                                                {
                                                    id: 'cpq.pricing-trace.fired-count',
                                                    defaultMessage: '{fired} of {total} fired',
                                                },
                                                {
                                                    fired: firedCount,
                                                    total: procedure.rules.length,
                                                },
                                            )}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {rules.length > 0 ? (
                                        <RuleTable rules={rules} />
                                    ) : (
                                        <Box paddingY={1}>
                                            <Typography variant="body2" color="textSecondary">
                                                {formatMessage({
                                                    id: 'cpq.pricing-trace.no-fired-rules',
                                                    defaultMessage:
                                                        'No rules fired. Enable "Show skipped rules" to see them.',
                                                })}
                                            </Typography>
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            ))}
        </Stack>
    );
}
